import {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  SyntheticEvent,
  useRef,
} from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  TextField,
  CircularProgress,
  Autocomplete,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Grid,
  Checkbox,
} from "@mui/material";
import { debounce } from "lodash";
import useFetchEmployeesList from "src/Pages/Company/OrganizationStructure/api/useFetchEmployeesList";
import useCheckCanBeAreaMembersManager from "src/Pages/Company/OrganizationStructure/api/useCheckCanBeAreaMembersManager";
import __ from "src/utils/Translations";
import {
  Employee,
  IAreaMembers,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import EmployeeChip from "src/Pages/Company/OrganizationStructure/Areas/Components/EmployeeChip";
import Alert from "@mui/material/Alert";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

interface IAutocompleteEmployees {
  companyId: string;
  structureId: string;
  dataTestId?: string;
}

const AutocompleteEmployees: FC<IAutocompleteEmployees> = ({
  companyId,
  structureId,
  dataTestId,
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const [filterString, setFilterString] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [persistentInvalidIds, setPersistentInvalidIds] = useState<string[]>(
    []
  );

  const enabled = !!companyId && !!structureId;
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error: employeesError,
  } = useFetchEmployeesList({ companyId, structureId, enabled, filterString });

  const membersWatch = watch("members") || [];
  const managerWatch = watch("manager");
  const parentWatch = watch("parent");

  const employeeId =
    managerWatch?.id !== "structure" ? managerWatch?.id : undefined;

  const { mutate: checkMembers, data: validationData } =
    useCheckCanBeAreaMembersManager(companyId, employeeId || "");

  const logStatus = useLogStatus();

  if (employeesError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "AutocompleteEmployees: fetchEmployeesList failed",
      statusDetails: {
        source: "API",
        operation: "fetchEmployeesList",
        status: (employeesError as AxiosError)?.response?.status,
        url: (employeesError as AxiosError)?.config?.url,
      },
    });
  }

  const { setValue } = useFormContext();

  useEffect(() => {
    if (validationData) {
      setValue(
        "_validationData",
        validationData.givenEmployeeCanInheritManager,
        { shouldDirty: false }
      );
    }
  }, [validationData, setValue]);

  const debouncedValidation = useRef(
    debounce(
      (
        companyId: string,
        employeeId: string,
        membersList: IAreaMembers[],
        parentAreaId: string | null
      ) => {
        if (
          companyId?.trim() &&
          employeeId?.trim() &&
          membersList?.length > 0
        ) {
          checkMembers({
            members: membersList.map((member) => member.id),
            parentArea: parentAreaId,
          });
        }
      },
      2000
    )
  ).current;

  useEffect(
    () => () => {
      debouncedValidation.cancel();
    },
    [debouncedValidation]
  );

  useEffect(() => {
    if (companyId?.trim() && employeeId?.trim() && membersWatch?.length > 0) {
      debouncedValidation(
        companyId,
        employeeId,
        membersWatch,
        parentWatch?.id || null
      );
    } else {
      setPersistentInvalidIds([]);
    }
  }, [companyId, employeeId, membersWatch, parentWatch, debouncedValidation]);

  useEffect(() => {
    if (!validationData) return;

    if (validationData.conflictingSubordinates.length > 0) {
      const conflictIds = validationData.conflictingSubordinates.map(
        (conflict) => conflict.employeeId
      );
      setPersistentInvalidIds(conflictIds);
    } else {
      setPersistentInvalidIds([]);
    }
  }, [validationData]);

  const options = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.content) || []).filter(
        (option) => option.id !== managerWatch?.id
      ),
    [data?.pages, managerWatch]
  );

  const debouncedSearch = useRef(
    debounce((_: SyntheticEvent, value: string) => {
      setFilterString(value);
    }, 300)
  ).current;

  const handleSearchChange = useCallback(
    (event: SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input") {
        setInputValue(newInputValue);
        debouncedSearch(event, newInputValue);
      } else if (reason === "clear") {
        setInputValue("");
        setFilterString("");
      }
    },
    [debouncedSearch]
  );

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  useEffect(() => {
    if (
      errors.members &&
      typeof errors.members === "object" &&
      !Array.isArray(errors.members)
    ) {
      const newInvalidIds = Object.keys(errors.members);
      setPersistentInvalidIds((prev) => {
        const combined = [...prev, ...newInvalidIds];
        return Array.from(new Set(combined));
      });
    }
  }, [errors.members]);

  useEffect(() => {
    const currentMemberIds = membersWatch.map(
      (member: IAreaMembers) => member.id
    );
    setPersistentInvalidIds((prev) =>
      prev.filter((id) => currentMemberIds.includes(id))
    );
  }, [membersWatch]);

  const handleScroll = (event: SyntheticEvent) => {
    const listbox = event.currentTarget as HTMLUListElement;
    const scrollPercentage =
      (listbox.scrollTop + listbox.clientHeight) / listbox.scrollHeight;

    if (scrollPercentage > 0.9 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      <Controller
        name="members"
        control={control}
        render={({ fieldState: { invalid, error }, field }) => (
          <Autocomplete
            multiple
            disableCloseOnSelect
            value={field.value || []}
            inputValue={inputValue}
            filterOptions={(options) => options}
            noOptionsText={
              isFetching ? __("Ładowanie...") : __("Brak wyników wyszukiwania")
            }
            getOptionLabel={(option) =>
              option?.name
                ? `${option.name}${option.fk ? ` (FK: ${option.fk})` : ""}`
                : ""
            }
            isOptionEqualToValue={(option, selectedValue) =>
              option.id === selectedValue?.id
            }
            options={options}
            onChange={(_, newValue) => {
              field.onChange(newValue);
              setInputValue("");
              setFilterString("");
            }}
            onInputChange={handleSearchChange}
            disableListWrap
            disablePortal
            renderInput={(params) => (
              <TextField
                {...params}
                date-testId={dataTestId}
                id="members"
                label={__("Dodaj pracownika")}
                error={invalid || persistentInvalidIds.length > 0}
                helperText={error?.message}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetching && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option: Employee, { selected }) => (
              <ListItem
                {...props}
                data-testId={`os__form-employee-${option.id}`}
                key={option.id}
                alignItems="center"
                sx={{ display: "flex", alignItems: "center", py: "6px" }}
              >
                <Box display="flex" alignItems="center" mr={2}>
                  <Checkbox checked={selected} />
                  <PresenceIndicator
                    status={option?.presenceInformation?.status}
                  >
                    <Avatar
                      size={6}
                      src={option.avatar || undefined}
                      alt={option.name || ""}
                    >
                      {option.initials ?? ""}
                    </Avatar>
                  </PresenceIndicator>
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={700}
                      fontSize="14px"
                    >
                      {`${option.name}${
                        option.fk ? ` (FK: ${option.fk})` : ""
                      }`}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontSize="12px"
                    >
                      {typeof option.position === "object" &&
                      option.position !== null
                        ? option.position.label
                        : option.position || null}
                    </Typography>
                  }
                />
              </ListItem>
            )}
            slotProps={{
              listbox: {
                onScroll: handleScroll,
                style: { maxHeight: "300px", overflowY: "auto" },
              },
            }}
            renderTags={() => null}
          />
        )}
      />
      {persistentInvalidIds.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            <Typography fontWeight={500}>
              {__(
                "Podświetleni pracownicy powodują konflikt z osobą zarządzającą."
              )}
            </Typography>
            <Typography variant="body2">
              {__("Zmień osobę zarządzającą lub usuń z obszaru pracowników.")}
            </Typography>
          </Alert>
        </Box>
      )}
      <Box>
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
          }}
        >
          {Array.isArray(membersWatch) &&
            membersWatch.length > 0 &&
            membersWatch.map((member: IAreaMembers) => {
              const isInvalid = persistentInvalidIds.includes(member.id ?? "");

              return (
                <Grid size={{ xs: 12, lg: 4 }} key={member.id ?? ""}>
                  <EmployeeChip
                    employeeName={member.name ?? ""}
                    fk={member.fk}
                    position={member.position?.label ?? ""}
                    area={member.area?.label ?? ""}
                    initials={member.initials ?? ""}
                    avatar={member.avatar ?? undefined}
                    employeeId={member.id ?? ""}
                    presenceInformation={member.presenceInformation}
                    isInvalid={isInvalid}
                  />
                </Grid>
              );
            })}
        </Grid>
      </Box>
    </>
  );
};

export default AutocompleteEmployees;
